/***************************************************************************************************
 * Load `$localize` onto the global scope - used if i18n tags appear in Angular templates.
 */
import '@angular/localize/init';

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js/dist/zone'; // Included with Angular CLI.
import * as formioDisplaysModule from 'formiojs/displays/Displays';
import * as emilyFormioDisplaysModule from 'formiojs-emilyemr/displays/Displays';

/***************************************************************************************************
 * APPLICATION IMPORTS
 */
import '@webcomponents/custom-elements/custom-elements.min';
(window as any)['global'] = window;

// HTMLCollection does not have a native forEach; polyfill so formio PDFBuilder can iterate children
const htmlCollectionPrototype = (HTMLCollection as any).prototype;
if (typeof HTMLCollection !== 'undefined' && !htmlCollectionPrototype.forEach) {
  htmlCollectionPrototype.forEach = Array.prototype.forEach;
}

// Runtime guard for Form.io PDF displays to avoid submit visibility crashes when submit is unresolved.
const patchPdfDisplayAttach = (displayModule: any) => {
  const Displays = displayModule?.default || displayModule;
  const PdfDisplay = Displays?.displays?.pdf;

  if (!PdfDisplay?.prototype || PdfDisplay.prototype.__emrSubmitVisibilityGuardApplied) {
    return false;
  }

  const originalAttach = PdfDisplay.prototype.attach;
  if (typeof originalAttach !== 'function') {
    return false;
  }

  const safeToggleSubmitVisibility = (instance: any) => {
    const refs = instance?.refs;
    const submitRef = refs?.submitButton || refs?.button;

    if (!submitRef?.classList || !Array.isArray(instance?.components)) {
      return;
    }

    const submitComponent = instance.components.find((component: any) => {
      return component && (component.element === refs?.submitButton || component.element === refs?.button);
    });

    submitRef.classList.toggle('hidden', submitComponent ? !submitComponent.visible : false);
  };

  PdfDisplay.prototype.attach = function patchedAttach(element: any) {
    const result = originalAttach.call(this, element);

    if (result && typeof result.then === 'function') {
      return result.then((value: any) => {
        safeToggleSubmitVisibility(this);
        return value;
      });
    }

    safeToggleSubmitVisibility(this);
    return result;
  };

  PdfDisplay.prototype.__emrSubmitVisibilityGuardApplied = true;
  return true;
};

patchPdfDisplayAttach(formioDisplaysModule);
patchPdfDisplayAttach(emilyFormioDisplaysModule);

const patchLegacyRemoveIcons = () => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return;
  }

  const legacyRemoveIconSvg = [
    '<svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">',
    '<path d="M256 512A256 256 0 1 1 256 0a256 256 0 1 1 0 512zM169.4 150.6a24 24 0 0 0-33.9 33.9L201 250l-65.6 65.4a24 24 0 0 0 33.9 33.9l65.7-65.5 65.6 65.5a24 24 0 0 0 33.9-33.9L269 250l65.5-65.4a24 24 0 0 0-33.9-33.9L235 216.2l-65.6-65.6z"></path>',
    '</svg>',
  ].join('');

  const patchRoot = (root: ParentNode | Document) => {
    const icons = (root as any).querySelectorAll?.('i.fa-times-circle-o') as NodeListOf<HTMLElement> | undefined;
    if (!icons?.length) {
      return;
    }

    icons.forEach((icon) => {
      if ((icon as any).__emrLegacyRemoveIconPatched) {
        return;
      }

      icon.classList.add('emr-legacy-remove-icon');
      icon.innerHTML = legacyRemoveIconSvg;
      icon.style.fontStyle = 'normal';
      icon.style.lineHeight = '1';
      icon.style.color = 'currentColor';
      (icon as any).__emrLegacyRemoveIconPatched = true;
    });
  };

  const patchIframe = (iframe: HTMLIFrameElement) => {
    const applyToIframeDocument = () => {
      try {
        const iframeDoc = iframe.contentDocument;
        if (!iframeDoc) {
          return;
        }

        patchRoot(iframeDoc);
      } catch {
        // Ignore cross-origin iframe access errors.
      }
    };

    iframe.addEventListener('load', applyToIframeDocument);
    applyToIframeDocument();
  };

  const startObserver = () => {
    patchRoot(document);
    document.querySelectorAll('iframe').forEach((iframe) => patchIframe(iframe as HTMLIFrameElement));

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node instanceof HTMLIFrameElement) {
            patchIframe(node);
            return;
          }

          if (node.matches('i.fa-times-circle-o')) {
            patchRoot(node.ownerDocument || document);
            return;
          }

          patchRoot(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver, { once: true });
    return;
  }

  startObserver();
};

patchLegacyRemoveIcons();
